import { AUTH_TOKEN_STORAGE_KEY } from "../../../lib/auth/session";
import { CHAT_FEATURE_ENABLED } from "./config";

function readFromStorages(key: string): string | null {
  if (typeof window === "undefined") return null;
  for (const storageKey of ["sessionStorage", "localStorage"]) {
    try {
      const store = (window as any)[storageKey] as Storage | undefined;
      if (!store) continue;
      const value = store.getItem(key);
      if (value) return value;
    } catch {
      // Ignore storage errors (e.g., disabled cookies).
    }
  }
  return null;
}

function clearFromStorages(key: string) {
  if (typeof window === "undefined") return;
  for (const storageKey of ["sessionStorage", "localStorage"]) {
    try {
      const store = (window as any)[storageKey] as Storage | undefined;
      store?.removeItem(key);
    } catch {
      // Clearing the cache is a best-effort operation.
    }
  }
}

function decodeBase64Url(segment: string): string {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");

  if (typeof atob === "function") {
    return atob(padded);
  }

  if (typeof Buffer !== "undefined") {
    return Buffer.from(padded, "base64").toString("utf8");
  }

  throw new Error("No base64 decoder available");
}

function parseJwt(token: string): Record<string, any> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = decodeBase64Url(parts[1]);
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload) return true;
  const exp = payload.exp;
  if (typeof exp !== "number") return true;
  const expiresAt = exp * 1000;
  return Number.isFinite(expiresAt) ? Date.now() >= expiresAt : true;
}

function extractAccessToken(rawValue: string | null): string | null {
  if (!rawValue) return null;

  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  const bearerMatch = /^Bearer\s+/i.exec(trimmed);
  const withoutPrefix = bearerMatch ? trimmed.slice(bearerMatch[0].length) : trimmed;
  const token = withoutPrefix.trim();

  return token || null;
}

function readValidToken(): string | null {
  const token = extractAccessToken(readFromStorages(AUTH_TOKEN_STORAGE_KEY));
  if (!token) {
    clearFromStorages(AUTH_TOKEN_STORAGE_KEY);
    return null;
  }

  if (isTokenExpired(token)) {
    clearFromStorages(AUTH_TOKEN_STORAGE_KEY);
    return null;
  }

  return token;
}

async function waitForTokenFromCache(timeoutMs = 5000, pollIntervalMs = 200): Promise<string | null> {
  if (typeof window === "undefined") {
    return null;
  }

  const existing = readValidToken();
  if (existing) {
    return existing;
  }

  return new Promise((resolve) => {
    const deadline = Date.now() + Math.max(timeoutMs, 0);
    let resolved = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      if (interval != null) {
        clearInterval(interval);
        interval = null;
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", handleStorageEvent);
      }
    };

    const finish = (value: string | null) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(value);
    };

    const checkCache = () => {
      const token = readValidToken();
      if (token) {
        finish(token);
        return;
      }

      if (Date.now() >= deadline) {
        finish(null);
      }
    };

    function handleStorageEvent(event: StorageEvent) {
      if (event.key === AUTH_TOKEN_STORAGE_KEY) {
        checkCache();
      }
    }

    window.addEventListener("storage", handleStorageEvent);

    interval = setInterval(checkCache, pollIntervalMs);
    checkCache();
  });
}

export class ChatTokenManager {
  async getToken(): Promise<string> {
    if (!CHAT_FEATURE_ENABLED) {
      throw new Error("Telemedicine chat is disabled");
    }

    const immediate = readValidToken();
    if (immediate) {
      return immediate;
    }

    const awaited = await waitForTokenFromCache();
    if (!awaited) {
      throw new Error("Telemedicine chat token is unavailable");
    }

    return awaited;
  }

  invalidate() {
    clearFromStorages(AUTH_TOKEN_STORAGE_KEY);
  }
}

export const chatTokenManager = new ChatTokenManager();

